import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Camera } from 'lucide-react';

// Justified row gallery layout algorithm
interface JustifiedImage extends GalleryImage {
  displayWidth: number;
  displayHeight: number;
}
function computeRows(
  images: GalleryImage[],
  containerWidth: number,
  rowHeight = 140,
  margin = 6
): JustifiedImage[][] {
  let rows: JustifiedImage[][] = [];
  let currentRow: JustifiedImage[] = [];
  let currentRowWidth = 0;
  for (let i = 0; i < images.length; i++) {
    const img = images[i];
    const aspect = img.width / img.height;
    const w = rowHeight * aspect;
    const justifiedImg: JustifiedImage = { ...img, displayWidth: w, displayHeight: rowHeight };
    currentRow.push(justifiedImg);
    currentRowWidth += w + margin;
    // If row is full or last image
    if (currentRowWidth > containerWidth || i === images.length - 1) {
      // Scale row to fit
      const scale = (containerWidth - margin * (currentRow.length - 1)) / (currentRowWidth - margin * currentRow.length);
      rows.push(currentRow.map(img => ({
        ...img,
        displayWidth: img.displayWidth * scale,
        displayHeight: img.displayHeight * scale
      })));
      currentRow = [];
      currentRowWidth = 0;
    }
  }
  return rows;
}

interface GalleryImage {
  id: string;
  src: string;
  title: string;
  width: number;
  height: number;
}

const imageFiles = [
  '501800539_122101332716894421_2367081317018895030_n.jpg',
  '502675578_122102687030894421_3551783189734728485_n.jpg',
  '502678202_122101318676894421_7070260923446582602_n.jpg',
  '502726432_122102687756894421_3289632876014305206_n.jpg',
  '502729018_122101332800894421_8601594144608030443_n.jpg',
  '503200161_122102687234894421_7707697830761715886_n.jpg',
  '503267872_122103882788894421_5287496202560780889_n.jpg',
  '503274520_122102686832894421_9168561466148334320_n.jpg',
  '503330028_122102687918894421_7462009615216422147_n.jpg',
  '503333204_122102687690894421_3172381687403555815_n.jpg',
  '503435307_122102686322894421_411105484585024275_n.jpg',
  '503492064_122101332854894421_2415781632110001124_n.jpg',
  '503546671_122103882428894421_1031574663436804739_n.jpg',
  '503564924_122102687186894421_6095176956703937663_n.jpg',
  '503580242_122103883652894421_6579174784565528371_n.jpg',
  '503600992_122102687426894421_1158529286932863975_n.jpg',
  '503753947_122103883874894421_1577497711551943658_n.jpg',
  '503777179_122103883088894421_5590635604525610999_n.jpg',
  '503809984_122106234404894421_6647438455668017029_n.jpg',
  '503847230_122103883166894421_5018476264500334781_n.jpg',
  '503851044_122106234806894421_1197343618911924495_n.jpg',
  '503888231_122106022784894421_8713902164651767766_n.jpg',
  '503940413_122106024728894421_4105765521253020780_n.jpg',
  '503966936_122106028892894421_797654627117557414_n.jpg',
  '503981353_122103883460894421_6278621475635540979_n.jpg',
  '503996645_122103883712894421_419937075339175736_n.jpg',
  '503998233_122106027842894421_2358557177991637715_n.jpg',
  '504058631_122106025808894421_5902964146154720368_n.jpg',
  '504078184_122106026816894421_126879091264880137_n.jpg',
  '504122187_122106234560894421_1924886093958651410_n.jpg',
  '504142342_122106234632894421_5748777667852641671_n.jpg',
  '504142356_122106023522894421_7348387410844138642_n.jpg',
  '504148248_122106023960894421_5053413545529391306_n.jpg',
  '504151069_122106023324894421_9069576834923801573_n.jpg',
  '504242392_122106028538894421_3777547014293696521_n.jpg',
  '504371879_122106024302894421_1623520574927852398_n.jpg',
  '504468784_122106029066894421_8078154710485244943_n.jpg',
  '504476501_122110440584894421_2217447707241535101_n.jpg',
  '504623186_122110440230894421_5812613772927340716_n.jpg',
  '504653066_122110440374894421_5470417872526236539_n.jpg',
  '504731977_122110441952894421_7547811555134542199_n.jpg',
  '504772356_122110960526894421_3382574608552215483_n.jpg',
  '504772396_122110441400894421_8098127615225487318_n.jpg',
  '504776418_122110441082894421_3963700154206103171_n.jpg',
  '504784941_122110439618894421_4342262746511921435_n.jpg',
  '504795856_122110439714894421_1405684139992556360_n.jpg',
  '504859566_122110960478894421_1249074833948422414_n.jpg',
  '504921828_122110439816894421_1717671395600477409_n.jpg',
  '504988084_122110441352894421_8508728024672516339_n.jpg',
  '505556316_122113032494894421_2353705482116971936_n.jpg',
  '505701543_122113031240894421_6389137289384799547_n.jpg',
  '505745788_122113031738894421_898014240081563636_n.jpg',
  '505817685_122113032440894421_8994951962878333932_n.jpg',
  '505876088_122113032308894421_2842808924666916293_n.jpg',
  '505925703_122113032038894421_5429697609801358890_n.jpg',
  '506162233_122113031780894421_7849810540585689856_n.jpg',
  '506206527_122113030688894421_319400195815087656_n.jpg',
  '506244826_122113031636894421_6094737873399321426_n.jpg',
  '506304555_122113032470894421_5600145636322372891_n.jpg',
  '506320298_122114030462894421_5348440756448227055_n.jpg',
  '506591154_122113031960894421_6695688597096455090_n.jpg',
  '506661811_122113031198894421_98306355321721845_n.jpg',
  '506701311_122114030582894421_2089798222410080458_n.jpg',
  '506895981_122114030726894421_5712132032932654277_n.jpg',
  '506980728_122114549486894421_6339577457585371587_n.jpg',
  '508121156_122114938040894421_3312625294903019011_n.jpg',
  '508155050_122114937704894421_8724139890859720425_n.jpg',
  '508159118_122114549348894421_1630509227448985223_n.jpg',
  '508163418_122114938196894421_8353494747693274916_n.jpg',
  '508170182_122114937662894421_6161556819141189024_n.jpg',
  '508183089_122114938154894421_6846634955233163194_n.jpg',
  '508187851_122114549444894421_6640743266280750056_n.jpg',
  '508192214_122114937806894421_1668331431173971115_n.jpg',
  '510769120_122118140462894421_2493263251682054862_n.jpg',
  '511051154_122118140510894421_7962443588570417555_n.jpg',
  '511153985_122118140288894421_7883902951076805309_n.jpg',
  '511175025_122118140390894421_7259531554117109317_n.jpg',
  '511177239_122118140702894421_3437513159504441208_n.jpg',
  '511192240_122118140330894421_6433008267038975910_n.jpg',
  '511193364_122118140654894421_3340435681391771410_n.jpg',
  '511719376_122118140246894421_4361681228196730410_n.jpg',
  '512485369_122118140564894421_8737091863225432783_n.jpg',
  '512512148_122118281978894421_8001873576061920959_n.jpg',
  '512521132_122118140864894421_8093700868478110222_n.jpg',
  '512568071_122118140750894421_8968430769871351101_n.jpg',
  '512580052_122118140822894421_7456033461267986099_n.jpg',
  '512629096_122118140606894421_6004858060742144596_n.jpg',
  '512660610_122119214912894421_6520292575848781430_n.jpg',
  '512694240_122119214804894421_2290614425323737576_n.jpg',
  '513920651_122120135876894421_4732779504794647668_n.jpg',
  '514033274_122119214702894421_516393306021528222_n.jpg',
  '514092374_122119190630894421_6526638085451393331_n.jpg',
  '514324914_122120135678894421_2340513409530144352_n.jpg',
  '514354620_122120135360894421_2647330839380838202_n.jpg',
  '514357615_122120135774894421_7214992833557991848_n.jpg',
  '514384484_122120135462894421_3962855883521877075_n.jpg',
  '514434463_122120135312894421_3589806820669036511_n.jpg',
  '514667895_122120134850894421_3936863162540860848_n.jpg',
  '514926636_122120135210894421_779785367565185208_n.jpg',
  '514965252_122120134898894421_1055639049843337501_n.jpg',
  '515000810_122120950640894421_919020062404827807_n.jpg',
  '515018053_122121205550894421_9134953256364997889_n.jpg',
  '515273128_122120949824894421_1745570961790689694_n.jpg',
  '515279825_122121205592894421_6000230025661626088_n.jpg',
  '515435968_122135990222894421_7314304614430503631_n.jpg',
  '515506491_122121205490894421_9007094738250559823_n.jpg',
  '515665820_122121205646894421_8625275984626995457_n.jpg',
  '515678090_122121205406894421_892962143695788007_n.jpg',
  '516006524_122121205448894421_159925553644879078_n.jpg',
  '516479208_122122643660894421_1044822797297997531_n.jpg',
  '516775629_122122643792894421_1330422690985010347_n.jpg',
  '516809588_122122643420894421_6245892520838970242_n.jpg',
  '517167293_122122843820894421_3842776183936466718_n.jpg',
  '518274169_122123718788894421_7523245694228790994_n.jpg',
  '518280503_122122643570894421_7333384311389351196_n.jpg',
  '518323939_122123135102894421_6972089302583462749_n.jpg',
  '518328644_122125264466894421_1377717465241622604_n.jpg',
  '518331488_122125264352894421_5352657708492723894_n.jpg',
  '518332241_122125264430894421_5812112072338398011_n.jpg',
  '518336243_122122643678894421_1265519126955877140_n.jpg',
  '518361900_122125264334894421_7802252551300137957_n.jpg',
  '518371832_122130677432894421_2232039075748260681_n.jpg',
  '518393330_122125264382894421_713183787378792198_n.jpg',
  '518395189_122130598988894421_8091397966457685924_n.jpg',
  '518412557_122125264490894421_4459992335281230993_n.jpg',
  '518414120_122126919224894421_1690773084941994768_n.jpg',
  '518419860_122126919398894421_8951715458858125728_n.jpg',
  '519503820_122126929904894421_5702166808644527948_n.jpg',
  '522700612_122126919422894421_5153949480379774539_n.jpg',
  '523330439_122126919296894421_8024785306961496419_n.jpg',
  '526585879_122130677456894421_1754804546236258659_n.jpg',
  '526842555_122130677240894421_7235968420886924465_n.jpg',
  '526861582_122130677162894421_4842873318822158305_n.jpg',
  '526931395_122130608324894421_2197955447206900055_n.jpg',
  '526984941_122130677396894421_3242280975562936293_n.jpg',
  '526985964_122130599114894421_2369645530002886007_n.jpg',
  '527057701_122130608204894421_5421552206678970731_n.jpg',
  '527183219_122130599054894421_255833147291689478_n.jpg',
  '527194175_122130677360894421_869042623802062781_n.jpg',
  '527345711_122130676976894421_2080034045200307239_n.jpg',
  '527454742_122130676898894421_3408635189557459812_n.jpg',
  '527470305_122130714056894421_2183155203508674264_n.jpg',
  '527482133_122130712886894421_3878087014708980401_n.jpg',
  '527536717_122130714272894421_913414475223461877_n.jpg',
  '527768142_122130608078894421_2739759310802999861_n.jpg',
  '527795017_122130677114894421_6923703162530188244_n.jpg',
  '528028081_122130712988894421_3844643702939934026_n.jpg',
  '528130294_122131691666894421_6340773266060120567_n.jpg',
  '528149786_122131692248894421_449545098173477715_n.jpg',
  '528153561_122130714122894421_8260806493955850461_n.jpg',
  '528270178_122131692074894421_8381892530059542745_n.jpg',
  '528309605_122131691750894421_6445022045662973968_n.jpg',
  '528358528_122130712940894421_8354046776991856507_n.jpg',
  '528384092_122131692116894421_811714289969607114_n.jpg',
  '528391471_122130713744894421_2524249958884016669_n.jpg',
  '528541020_122131691918894421_3773394945371335588_n.jpg',
  '528827047_122131691810894421_1976540340792452936_n.jpg',
  '529253743_122131692356894421_8615790680045910871_n.jpg',
  '529274983_122132237636894421_5807805508536455571_n.jpg',
  '531203597_122133041960894421_2473079745146307505_n.jpg',
  '533486561_122135414396894421_4390423691105000781_n.jpg',
  '534770205_122135414480894421_894598396722093464_n.jpg',
  '535586928_122135949752894421_1780901208583023347_n.jpg',
  '535897656_122135949266894421_7553101220881593703_n.jpg',
  '535972913_122135949476894421_9126016965722592860_n.jpg',
  '535992776_122135990594894421_3160075305655092500_n.jpg',
  '536231158_122135948582894421_161523537907439599_n.jpg',
  '536280332_122135990936894421_4170368983863949898_n.jpg',
  '536333755_122135949086894421_1565246164600698613_n.jpg',
  '536365585_122135948672894421_799909124298441397_n.jpg',
  '536444195_122135990906894421_114747561274353740_n.jpg',
  '536589151_122135949446894421_5250899436308134749_n.jpg',
  '536589157_122135991002894421_5201525573033761228_n.jpg',
  '536618371_122135990576894421_4378773443785024511_n.jpg',
  '536643663_122135949428894421_1706463946864023721_n.jpg',
  '536681445_122135950106894421_4792037549976701490_n.jpg',
  '536823191_122135948936894421_4537127594810193377_n.jpg',
  '536864131_122135990618894421_2004229139000835028_n.jpg',
  '537032416_122135990324894421_6047564275854757804_n.jpg',
  '537253735_122135949218894421_3125449423953981521_n.jpg',
  '537625196_122135949164894421_6897974836659436477_n.jpg',
  '537898226_122135949674894421_2043573100534575460_n.jpg',
  '539433515_122138057522894421_6983302330616047725_n.jpg',
  '540537239_122138056598894421_3009316387091105177_n.jpg',
  '540679457_122138069114894421_8640509355207930530_n.jpg',
  '540740259_122138057600894421_6288340907873703640_n.jpg',
  '540765335_122138057096894421_8179599021987625742_n.jpg',
  '540953240_122138056760894421_2494527221245249555_n.jpg',
  '540968017_122138057198894421_878429396144330044_n.jpg',
  '540999111_122138057168894421_4895018373197375553_n.jpg',
  '541076419_122137820588894421_3530684816246438593_n.jpg',
  '541132903_122137820480894421_6446548295073523445_n.jpg',
  '541141987_122138057762894421_6128102954546020270_n.jpg',
  '541468843_122137822202894421_8123994900046097882_n.jpg',
  '542684462_122138682782894421_2352138189489501090_n.jpg',
  '543213489_122138849192894421_8810735598398547584_n.jpg',
];

// For demo, use a fixed aspect ratio for all images (or you can fetch real dimensions)
const DEMO_WIDTH = 400;
const DEMO_HEIGHT = 300;

interface GalleryProps {
  showAll?: boolean;
}

const Gallery: React.FC<GalleryProps> = ({ showAll }) => {
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(900);
  const containerRef = useRef<HTMLDivElement>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0])); // Start with first image
  const [isImageLoading, setIsImageLoading] = useState(false);

  // Build images array with fake aspect ratios (for real: use actual image sizes)
  let images: GalleryImage[] = imageFiles.map((filename, index) => ({
    id: `img-${index}`,
    src: `${process.env.PUBLIC_URL}/gallery/${filename}`,
    title: `Classic Mini Restoration Project ${index + 1}`,
    width: DEMO_WIDTH,
    height: DEMO_HEIGHT - (index % 3) * 40 // Vary height for demo
  }));
  
  // For homepage, use ALL images in carousel
  const carouselImages = !showAll ? images : [];
  
  // Keep the grid gallery only for "See All" page
  if (!showAll) {
    // Don't slice - use all images for carousel
  } else {
    // For "See All" page, keep the grid layout
  }

  // Responsive: update container width on resize
  useEffect(() => {
    function updateWidth() {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    }
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Justified rows (only for showAll mode)
  const rows = computeRows(images, containerWidth, 120, 8);

  const openLightbox = (img: GalleryImage, idx: number) => {
    setSelectedImage(img);
    setCurrentIndex(idx);
  };
  const closeLightbox = () => setSelectedImage(null);
  const navigateImage = (dir: 'prev' | 'next') => {
    let newIdx = dir === 'prev' ? (currentIndex - 1 + images.length) % images.length : (currentIndex + 1) % images.length;
    setCurrentIndex(newIdx);
    setSelectedImage(images[newIdx]);
  };

  // Carousel navigation - only navigate to preloaded images
  const handleCarouselNext = () => {
    const nextIndex = (carouselIndex + 1) % carouselImages.length;
    
    // Only navigate if image is already loaded
    if (loadedImages.has(nextIndex)) {
      setCarouselIndex(nextIndex);
    } else {
      // Start loading if not loaded yet
      setIsImageLoading(true);
      const img = new Image();
      img.src = carouselImages[nextIndex].src;
      img.onload = () => {
        setLoadedImages(prev => new Set([...prev, nextIndex]));
        setCarouselIndex(nextIndex);
        setIsImageLoading(false);
      };
      img.onerror = () => {
        setIsImageLoading(false);
      };
    }
  };

  const handleCarouselPrev = () => {
    const prevIndex = (carouselIndex - 1 + carouselImages.length) % carouselImages.length;
    
    // Only navigate if image is already loaded
    if (loadedImages.has(prevIndex)) {
      setCarouselIndex(prevIndex);
    } else {
      // Start loading if not loaded yet
      setIsImageLoading(true);
      const img = new Image();
      img.src = carouselImages[prevIndex].src;
      img.onload = () => {
        setLoadedImages(prev => new Set([...prev, prevIndex]));
        setCarouselIndex(prevIndex);
        setIsImageLoading(false);
      };
      img.onerror = () => {
        setIsImageLoading(false);
      };
    }
  };

  // Preload next image in background
  useEffect(() => {
    if (!showAll && carouselImages.length > 0 && !isImageLoading) {
      const nextIndex = (carouselIndex + 1) % carouselImages.length;
      
      // If next image isn't loaded yet, preload it
      if (!loadedImages.has(nextIndex)) {
        const img = new Image();
        img.src = carouselImages[nextIndex].src;
        img.onload = () => {
          setLoadedImages(prev => new Set([...prev, nextIndex]));
        };
      }
    }
  }, [carouselIndex, showAll, carouselImages, loadedImages, isImageLoading]);

  // Auto-advance carousel only when next image is ready
  useEffect(() => {
    if (!showAll && carouselImages.length > 0 && !isImageLoading) {
      const nextIndex = (carouselIndex + 1) % carouselImages.length;
      
      // Only set interval if next image is already loaded
      if (loadedImages.has(nextIndex)) {
        const interval = setInterval(() => {
          setCarouselIndex(nextIndex);
        }, 5000);
        return () => clearInterval(interval);
      }
    }
  }, [carouselIndex, showAll, loadedImages, isImageLoading, carouselImages.length]);

  return (
    <section id="gallery" className="section-padding bg-gray-50">
      <div className="container-custom">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center space-x-2 bg-mini-red/10 text-mini-red px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Camera size={16} />
            <span>Our Work</span>
          </div>
          <h2 className="text-3xl sm:text-4xl section-heading text-gray-900 mb-6">
            Gallery of Classic Minis
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            Take a look at some of the beautiful classic Minis we've had the pleasure of working on.
          </p>
        </motion.div>
        
        {/* Carousel for homepage (3 images) */}
        {!showAll && carouselImages.length > 0 && (
          <div className="relative max-w-4xl mx-auto px-4">
            <div className="relative aspect-[16/10] rounded-xl overflow-hidden shadow-2xl bg-gray-200">
              <AnimatePresence mode="wait">
                <motion.div
                  key={carouselIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0"
                >
                  <img
                    src={carouselImages[carouselIndex].src}
                    alt={carouselImages[carouselIndex].title}
                    className="w-full h-full object-cover"
                    loading={carouselIndex === 0 ? 'eager' : 'lazy'}
                  />
                  {isImageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
              
              {/* Navigation Buttons */}
              <button
                onClick={handleCarouselPrev}
                disabled={isImageLoading}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Previous image"
              >
                <ChevronLeft size={28} />
              </button>
              <button
                onClick={handleCarouselNext}
                disabled={isImageLoading}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Next image"
              >
                <ChevronRight size={28} />
              </button>
              
              {/* Indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {carouselImages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      if (!isImageLoading && loadedImages.has(idx)) {
                        setCarouselIndex(idx);
                      }
                    }}
                    className={`w-3 h-3 rounded-full transition-all ${
                      idx === carouselIndex 
                        ? 'bg-white w-8' 
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                    aria-label={`Go to image ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Justified Row Gallery (for "See All" page) */}
        {showAll && (
          <div ref={containerRef} className="w-full mx-auto px-1 sm:px-2 md:px-4">
            {rows.map((row, rowIdx) => (
              <div key={rowIdx} className="flex flex-row gap-1 sm:gap-2 mb-2 justify-center">
                {row.map((img, idx) => (
                  <motion.div
                    key={img.id}
                    className="relative cursor-pointer overflow-hidden rounded-md bg-gray-100 border border-gray-200 shadow group"
                    style={{ width: img.displayWidth, height: img.displayHeight, minWidth: 40 }}
                    whileHover={{ scale: 1.03, zIndex: 2 }}
                    onClick={() => openLightbox(img, images.findIndex(i => i.id === img.id))}
                  >
                    <img
                      src={img.src}
                      alt={img.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                      style={{ background: '#e5e7eb' }}
                    />
                  </motion.div>
                ))}
              </div>
            ))}
          </div>
        )}
        {/* See All Button */}
        {!showAll && (
          <div className="flex justify-center mt-8">
            <a
              href="/all-gallery"
              className="inline-block px-6 py-3 bg-mini-red text-white font-semibold rounded-full shadow hover:bg-mini-green transition-colors duration-200"
            >
              See All Photos
            </a>
          </div>
        )}
        {/* Lightbox */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeLightbox}
            >
              <motion.div
                className="relative max-w-4xl max-h-[90vh] w-full"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                onClick={e => e.stopPropagation()}
              >
                <img
                  src={selectedImage.src}
                  alt={selectedImage.title}
                  className="w-full h-full object-contain rounded-lg"
                />
                {/* Close Button */}
                <button
                  onClick={closeLightbox}
                  className="absolute top-4 right-4 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                >
                  <X size={24} />
                </button>
                {/* Navigation */}
                <button
                  onClick={() => navigateImage('prev')}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={() => navigateImage('next')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                >
                  <ChevronRight size={24} />
                </button>
                {/* Image Info */}
                <div className="absolute bottom-4 left-4 right-4 bg-black/50 text-white p-4 rounded-lg">
                  <h3 className="text-lg font-semibold">{selectedImage.title}</h3>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

export default Gallery;
